import React, { useEffect, useState } from "react";
import { useApi } from "../hooks/useApi";
import { useModuleAvailability } from "../hooks/useModuleAvailability";
import { bandTone } from "./IdentityExposure";

const DIMENSION_LABEL = {
  findings: "Open findings",
  privilege: "Privilege",
  posture: "Posture weaknesses",
  surface: "What it can reach",
  hygiene: "Dormancy",
  proximity: "Attack-path proximity",
};

const money = (value) =>
  typeof value !== "number" || value <= 0 ? null
    : value >= 1_000_000 ? `$${(value / 1_000_000).toFixed(1)}M`
    : value >= 1_000 ? `$${Math.round(value / 1_000)}K`
    : `$${Math.round(value)}`;

/**
 * The card's headline: the "What is wrong" entries counted by severity, so the top of the card and
 * the list under it can never disagree. An empty list with unread evidence is not "nothing wrong".
 */
function ProblemSummary({ problems, notKnown }) {
  const list = problems || [];
  const count = (severity) => list.filter(p => p.severity === severity).length;
  const counts = [["Critical", count("Critical")], ["High", count("High")], ["Medium", count("Medium")]];
  const unchecked = notKnown?.length > 0;

  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[11px]" data-testid="exposure-problem-chips">
      {list.length === 0 ? (
        <span className="text-gray-600">
          {unchecked ? "No problems found in what could be checked" : "No problems found"}
        </span>
      ) : (
        <>
          <span className="text-gray-500">Problems:</span>
          {counts.map(([severity, n]) => (
            <span key={severity} className={`px-1.5 py-0.5 rounded border font-medium ${bandTone(severity)}`}>
              {n} {severity.toLowerCase()}
            </span>
          ))}
        </>
      )}
      {unchecked && (
        <span className="px-1.5 py-0.5 rounded border bg-amber-50 text-amber-800 border-amber-200">
          some checks not run
        </span>
      )}
    </div>
  );
}

/**
 * What is wrong across the estate, one entry per rule rather than per account.
 *
 * <p>The card used to lead with a ranked list and a band strip, which on a real tenant reads
 * "0 critical, 0 high, 1 medium, 607 low" — six hundred accounts in one bucket is a directory
 * listing, not a ranking, and it left the reader with no problem to act on. A rule that fired on
 * forty accounts is ONE thing to decide about; the examples are there so it can be checked against
 * a real account instead of taken on trust.</p>
 */
function Problems({ problems, notKnown, onOpenIdentity }) {
  if (!problems?.length && !notKnown?.length) return null;

  return (
    <div data-testid="exposure-problems">
      <div className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">
        What is wrong
        <span className="font-normal normal-case tracking-normal text-gray-400"
          title="How serious the problem itself is. The account score bands further down are a separate measure.">
          {" "}· severity of the problem
        </span>
      </div>
      <ul className="space-y-1 mt-1">
        {problems.map(p => (
          <li key={p.key} className="border border-gray-200 rounded-md p-2" data-testid={`problem-${p.key}`}>
            <div className="flex items-start gap-2">
              <span className={`px-1.5 py-0.5 rounded border text-[11px] font-semibold flex-shrink-0 ${bandTone(p.severity)}`}>
                {p.severity}
              </span>
              <span className="text-xs font-semibold text-gray-900 flex-1">{p.title}</span>
              {p.scope === "organization"
                ? <span className="text-[11px] text-gray-500 flex-shrink-0">whole tenant</span>
                : <span className="text-[11px] text-gray-500 flex-shrink-0">
                    {p.identities} account{p.identities === 1 ? "" : "s"}
                  </span>}
            </div>
            {p.why && <p className="text-[11px] text-gray-600 mt-1 leading-relaxed">{p.why}</p>}
            <p className="text-[11px] text-gray-800 mt-1 leading-relaxed">
              <span className="font-semibold">What to do: </span>{p.action}
            </p>
            {/* The accounts behind the problem — the one part of this block someone acts on, and
                the way into each drawer. At 11px grey they read as a footnote; they are the point. */}
            {p.examples?.length > 0 && (
              <div className="text-sm text-gray-600 mt-1.5" data-testid={`problem-accounts-${p.key}`}>
                {p.examples.map((example, index) => (
                  <React.Fragment key={example.identityId}>
                    {index > 0 && ", "}
                    <button type="button"
                      className="font-medium text-indigo-700 underline hover:text-indigo-900"
                      onClick={() => onOpenIdentity?.(example.identityId)}>{example.name}</button>
                  </React.Fragment>
                ))}
                {p.identities > p.examples.length && ` and ${p.identities - p.examples.length} more`}
              </div>
            )}
          </li>
        ))}
      </ul>
      {notKnown?.length > 0 && (
        <div className="text-[11px] text-amber-700 mt-1" data-testid="problems-not-known">
          Not checked, because the evidence was not read: {notKnown.join(", ")}.
        </div>
      )}
    </div>
  );
}

/**
 * The stored history as one line: where the estate's exposure has moved, drawn from the daily
 * snapshots. Two points are the minimum — a single day is a reading, not a trend — and when the
 * share of the model that could be measured changed during the period the card says so, because
 * part of that movement is a change in evidence rather than in the estate.
 */
function ExposureTrend({ trend, days }) {
  const points = trend?.points ?? [];
  if (points.length < 2) return null;

  const values = points.map(p => (typeof p.exposure === "number" ? p.exposure : 0));
  const first = values[0];
  const last = values[values.length - 1];
  const delta = last - first;
  const max = Math.max(...values, 1);
  const min = Math.min(...values);
  const span = Math.max(max - min, 1);

  // A plain polyline: a sparkline needs no chart library, and one fewer dependency in a card that
  // renders inside a 600-row page is worth more than the styling a library would add.
  const width = 120, height = 20;
  const path = values
    .map((v, i) => `${(i / (values.length - 1)) * width},${height - ((v - min) / span) * height}`)
    .join(" ");

  const direction = delta === 0 ? "unchanged" : delta < 0 ? "down" : "up";
  const tone = delta === 0 ? "text-gray-500" : delta < 0 ? "text-emerald-700" : "text-amber-700";

  return (
    <div className="flex items-center gap-2 text-[11px]" data-testid="exposure-trend">
      <svg width={width} height={height} className="text-indigo-400 flex-shrink-0" aria-hidden="true">
        <polyline points={path} fill="none" stroke="currentColor" strokeWidth="1.5" />
      </svg>
      <span className={tone}>
        Exposure {direction}
        {delta !== 0 && ` ${money(Math.abs(delta)) || Math.abs(Math.round(delta))}`} over the last {days} days
      </span>
      {trend?.note && (
        <span className="text-amber-700" data-testid="exposure-trend-note" title={trend.note}>
          · evidence coverage changed
        </span>
      )}
    </div>
  );
}

/**
 * Fork 2: the estate's exposure and the accounts worth opening first.
 *
 * Two things this card is careful about. The money is the platform's deduplicated figure — a risk
 * naming five accounts is one exposure, so the total is deliberately NOT the sum of the rows
 * beneath it. And the coverage line states how much of the model this tenant could be measured on,
 * because a ranking built on three of five dimensions is still useful but must not be read as the
 * whole picture.
 */
/**
 * The card opens CLOSED on every page load, and that is deliberate rather than a remembered
 * preference. The identity list underneath is what this tab is for; the card's detail runs long
 * enough to push it off the first screen, so it starts out of the way every time and is opened by
 * whoever wants it. A remembered "open" would quietly undo that for anyone who ever expanded it
 * once.
 *
 * The summary — the money and the band chips — stays on screen either way, so a closed card still
 * answers "how bad is it" without a click.
 */
export default function IdentityExposureCard({ limit = 8, trendDays = 30, onOpenIdentity }) {
  const apiCall = useApi();
  // Defender for Identity needs a vendor API: where the installation cannot reach it the card is
  // not shown and fetches nothing (nor before the flags load).
  const { loaded: modulesLoaded, isModuleAvailable } = useModuleAvailability();
  const available = modulesLoaded && isModuleAvailable("defenderIdentity");
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);   // null = loading
  const [trend, setTrend] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!available) return undefined;
    let cancelled = false;
    apiCall("GET", `/defender-identity/exposure/summary?limit=${limit}`)
      .then(r => { if (!cancelled) (r && typeof r.measured === "number" ? setData(r) : setFailed(true)); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [apiCall, limit, available]);

  // The stored history. Its absence is normal — a tenant has no trend until it has synced twice —
  // so a failure here is silent and the card renders without the line.
  useEffect(() => {
    if (!available) return undefined;
    let cancelled = false;
    apiCall("GET", `/defender-identity/exposure/trend?days=${trendDays}`)
      .then(r => { if (!cancelled && Array.isArray(r?.points)) setTrend(r); })
      .catch(() => { /* no trend line, card still useful */ });
    return () => { cancelled = true; };
  }, [apiCall, trendDays, available]);

  if (!available) return null;
  if (failed) return null;   // the inventory below is still the useful thing on this tab

  if (data === null) {
    return (
      <div className="border border-gray-200 rounded-lg p-3 bg-white" data-testid="exposure-card-loading">
        <div className="text-xs text-gray-400">Loading exposure…</div>
      </div>
    );
  }

  const toggle = () => setOpen((current) => !current);

  const observedPct = Math.round((data.observedWeight ?? 0) * 100);
  const total = data.measured + data.notMeasurable;

  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-white space-y-2" data-testid="identity-exposure-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          {/* The heading is the toggle. What collapses is the detail below — the money and the problem
              counts stay on screen, so a closed card still answers "how bad is it" without a click. */}
          {/* Sized to be noticed. It is the only way into what is wrong on this estate, and at the
              body text size it read as a label rather than as something to open. */}
          <button type="button" onClick={toggle} aria-expanded={open}
            className="flex items-center gap-2 text-lg font-bold text-gray-900 hover:text-indigo-700"
            data-testid="identity-exposure-toggle">
            <span className={`text-sm text-indigo-500 transition-transform ${open ? "rotate-90" : ""}`}>▶</span>
            Identity exposure
            {!open && (
              <span className="text-xs font-medium text-indigo-600 underline decoration-dotted">
                show details
              </span>
            )}
          </button>
          {/* The old subtitle read "Ranking only — no finding is created and no control moves",
              which told the reader the block changes nothing. True of the ranking, and it buried
              the part they can act on. The guarantee still belongs on the card — it just does not
              belong in the headline. */}
          <div className="text-[11px] text-gray-500">
            What is wrong, and which accounts to look at first.
          </div>
        </div>
        {money(data.exposure) && (
          <div className="text-right">
            <div className="text-sm font-semibold text-gray-900">{money(data.exposure)}</div>
            <div className="text-[11px] text-gray-500" title="Each risk counted once, however many accounts it names">
              across {data.measured} measured account{data.measured === 1 ? "" : "s"}
              {data.risksWithoutEstimate > 0 ? ` · ${data.risksWithoutEstimate} finding(s) without an estimate` : ""}
            </div>
          </div>
        )}
      </div>

      {/* The headline counts PROBLEMS, the same entries "What is wrong" lists, so the number at the
          top always matches the list under it. The account score bands used to sit here and read
          "0 critical" above a Critical problem: both true, on different scales, and nothing on
          screen could make that look like anything but a contradiction. They now sit with the
          ranking they actually order. */}
      <ProblemSummary problems={data.problems} notKnown={data.problemsNotKnown} />

      {open && (
        <>
        <ExposureTrend trend={trend} days={trendDays} />

        {/* Not decoration: without this line a three-dimension ranking reads as the whole picture. */}
        <div className="text-[11px] text-gray-500">
          Measured on {observedPct}% of the model
          {data.notKnown?.length > 0 && ` · not known: ${data.notKnown.map(k => DIMENSION_LABEL[k] || k).join(", ")}`}
        </div>

        <Problems problems={data.problems} notKnown={data.problemsNotKnown} onOpenIdentity={onOpenIdentity} />

        {data.reductions?.length > 0 && (
          <div data-testid="exposure-reductions">
            <div className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Worth closing first</div>
            <ul className="space-y-0.5 mt-0.5">
              {data.reductions.slice(0, 3).map(r => (
                <li key={r.unifiedRiskId} className="text-[11px] text-gray-700 flex items-center gap-2">
                  <span className="truncate flex-1">{r.title}</span>
                  <span className="text-gray-500 flex-shrink-0">
                    {/* Defender raises one incident per detection, so the same condition arrives as
                        several findings with one title. They are one investigation, so they are one
                        row — saying how many stops the combined figure looking wrong. */}
                    {r.findings > 1 && `${r.findings} findings · `}
                    {r.identitiesAffected} account{r.identitiesAffected === 1 ? "" : "s"}
                    {r.privilegedAffected > 0 && `, ${r.privilegedAffected} privileged`}
                    {r.identitiesImproved > 0 && ` · ${r.identitiesImproved} would improve`}
                  </span>
                  {money(r.exposureRemoved) && (
                    <span className="text-emerald-700 font-medium flex-shrink-0">{money(r.exposureRemoved)}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <div className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Worth looking at first</div>
          {/* Kept, but demoted: the ranking is a sort order, and saying so here stops a reader taking
              a high score for a finding. The verdicts above are the part that names a problem. */}
          <div className="text-[11px] text-gray-400">
            Ranking only — no finding is created and no control moves.
          </div>
          {/* The score bands belong to this ranking: each account's blended 0–100 score, which is
              what orders the list below. A problem's severity above is a separate measure. */}
          <div className="flex flex-wrap items-center gap-1.5 text-[11px] mt-1" data-testid="exposure-band-chips">
            <span className="text-gray-500"
              title="Each account's 0–100 exposure score, cut into bands (critical 80+, high 70+, medium 45+). One serious problem is only part of an account's score, so these bands can be lower than the problems above.">
              Accounts by exposure score:
            </span>
            {[["Critical", data.critical], ["High", data.high], ["Medium", data.medium], ["Low", data.low]].map(([band, count]) => (
              <span key={band} className={`px-1.5 py-0.5 rounded border font-medium ${bandTone(band)}`}>
                {count} {band.toLowerCase()}
              </span>
            ))}
            {data.privilegedAtRisk > 0 && (
              <span className="px-1.5 py-0.5 rounded border bg-rose-50 text-rose-700 border-rose-200 font-medium">
                {data.privilegedAtRisk} privileged at high or critical
              </span>
            )}
            {data.notMeasurable > 0 && (
              <span className="px-1.5 py-0.5 rounded border bg-gray-100 text-gray-600 border-gray-200">
                {data.notMeasurable} of {total} could not be measured
              </span>
            )}
          </div>
        </div>

        {data.top.length === 0 ? (
          <div className="text-xs text-gray-400" data-testid="exposure-card-empty">
            {data.measured === 0
              ? "No identity could be measured yet — run a sync first."
              : "No measured identity scores above zero."}
          </div>
        ) : (
          <ol className="space-y-0.5" data-testid="exposure-card-rows">
            {data.top.map((row, index) => (
              <li key={row.identityId}>
                <button
                  type="button"
                  onClick={() => onOpenIdentity?.(row.identityId)}
                  className="w-full flex items-center gap-2 text-xs px-1.5 py-1 -mx-1.5 rounded hover:bg-gray-50 text-left"
                >
                  <span className="text-gray-400 tabular-nums w-4">{index + 1}</span>
                  <span className="font-medium text-gray-900 truncate flex-1">
                    {row.displayName || row.accountUpn || `Identity ${row.identityId}`}
                  </span>
                  {row.isPrivileged && (
                    <span className="px-1 rounded bg-rose-50 text-rose-700 border border-rose-200 flex-shrink-0">Privileged</span>
                  )}
                  {money(row.monetaryImpact) && <span className="text-gray-500 flex-shrink-0">{money(row.monetaryImpact)}</span>}
                  <span className={`px-1.5 py-0.5 rounded border font-semibold flex-shrink-0 ${bandTone(row.band)}`}>
                    {typeof row.score === "number" ? row.score : "—"}
                  </span>
                </button>
              </li>
            ))}
          </ol>
        )}
        </>
      )}
    </div>
  );
}
